import { useState, useEffect } from "react";
import { StatusBadge } from "./StatusBadge";
import { formatCurrency, formatDate, formatPhone, isPhoneKey } from "../lib/utils-format";
import { Skeleton } from "./ui/skeleton";
import { Inbox, ChevronLeft, ChevronRight } from "lucide-react";

function renderCell(col, row) {
  if (col.render) return col.render(row);
  const v = row[col.key];
  if (col.badge) return <StatusBadge value={v} />;
  if (col.type === "currency") return formatCurrency(v);
  if (col.type === "date") return formatDate(v);
  if (col.type === "boolean") {
    const yes = v === true || v === "true";
    return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${yes ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>{yes ? "Yes" : "No"}</span>;
  }
  if (v === undefined || v === null || v === "") return <span className="text-muted-foreground">—</span>;
  if (isPhoneKey(col.key)) return formatPhone(v);
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function pageRange(current, count) {
  const delta = 1;
  const nums = [];
  for (let i = 1; i <= count; i++) {
    if (i === 1 || i === count || (i >= current - delta && i <= current + delta)) nums.push(i);
  }
  const out = [];
  let prev;
  for (const i of nums) {
    if (prev && i - prev > 1) out.push(`gap-${i}`);
    out.push(i);
    prev = i;
  }
  return out;
}

export default function DataTable({ columns, items, loading, onRowClick, emptyLabel = "No records yet", pageSize = 10 }) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, pageCount);

  // Reset to first page when the result set size changes (search / filter / reload).
  useEffect(() => { setPage(1); }, [items.length]);

  const start = (current - 1) * pageSize;
  const pageItems = loading ? items : items.slice(start, start + pageSize);
  const showPagination = !loading && items.length > pageSize;

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto slim-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {columns.map((c) => (
                  <th key={c.key} className="text-left font-semibold text-[11px] uppercase tracking-[0.1em] text-muted-foreground px-4 py-3 whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-border">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-16 text-center">
                    <Inbox className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">{emptyLabel}</p>
                  </td>
                </tr>
              ) : (
                pageItems.map((row, idx) => (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick?.(row)}
                    data-testid={`row-${row.id}`}
                    className="border-b border-border last:border-0 hover:bg-accent/50 cursor-pointer transition-colors fade-up"
                    style={{ animationDelay: `${Math.min(idx * 25, 300)}ms` }}
                  >
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 whitespace-nowrap">{renderCell(c, row)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPagination && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="pagination">
          <p className="text-xs text-muted-foreground" data-testid="pagination-info">
            Showing <span className="font-medium text-foreground">{start + 1}</span>–<span className="font-medium text-foreground">{Math.min(start + pageSize, items.length)}</span> of <span className="font-medium text-foreground">{items.length}</span>
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={current === 1}
              data-testid="pagination-prev"
              className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-sm font-medium hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            {pageRange(current, pageCount).map((n) =>
              typeof n === "string" ? (
                <span key={n} className="px-1.5 text-muted-foreground select-none">…</span>
              ) : (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  data-testid={`pagination-page-${n}`}
                  className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium transition-colors ${n === current ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"}`}
                >
                  {n}
                </button>
              )
            )}
            <button
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={current === pageCount}
              data-testid="pagination-next"
              className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-sm font-medium hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
